import * as React from 'react';
import { cloneDeep } from 'lodash';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  Reference,
  FrameItem,
  ReferenceNode,
  Attribute,
  ParameterType,
  ConditionContent,
  TriggerCondition,
  DataQuery
} from '@interface/index';
import { NiTable } from '@component/index';
import { localizedString } from '@locale/index';
import {
  NiCommonCascader,
  SELECTION_KEY,
  SELECTION_VALUE_TYPE,
  SelectionOption,
  SpecialValueType,
  Operation,
  renderSelectionVariable
} from '@config/index';
import {
  ParameterMapStore,
  isSystemBoolean,
  dataQueryToParameter,
  isEqual
} from '@lib/index';

import { OperationColumnComp } from './trigger-table';
import { ValueSwitch } from './value-switch';

import './condition-table.less';

type ConditionRow = {
  variable: Reference;
  realIndex: number;
  contentIndex: number;
  rowSpan: number;
  operation: Operation;
  operationAt?: string;
  value: any;
};

export type ConditionTableProps = {
  style?: React.CSSProperties;
  data: TriggerCondition[];
  disabled?: boolean;
  frame: FrameItem;
  attribute?: Attribute;
  selectedParameters: Attribute[];
  dataQueries: DataQuery[];
  onChange: (next: TriggerCondition[]) => void;
};

export class ConditionTable extends React.Component<ConditionTableProps> {

  flattenData = (source: TriggerCondition[]) => {

    const ret: ConditionRow[] = [];
    for (const [i, variable] of source.entries()) {
      for (const [j, content] of variable.conditions.entries()) {
        ret.push({
          variable: variable.variable,
          realIndex: i,
          contentIndex: j,
          operation: content.operation,
          operationAt: content.operationAt,
          value: content.value,
          rowSpan: j === 0 ? variable.conditions.length : 0
        });
      }
    }

    return ret;
  }

  getValueComp = (value: any, record: ConditionRow, index: number) => {
    const props = this.props;

    // if (!record.variable.length) {
    //   return '';
    // }

    const condition = props.data[record.realIndex].conditions[record.contentIndex];

    const isTable = condition.operationAt === 'table_row' || false;

    const node = record && record.variable[record.variable.length - 1];
    const param = node && ParameterMapStore.get(node.id);
    const isMultiValue = node && !node.showIndex && param && param.multiValue || false;
    const isBoolean = param
      && param.proto
      && isSystemBoolean(param.proto) || false;

    return (
      <div
        style={{
          position: 'relative',
          padding: '0 20px 0 0',
          minHeight: 32
        }}
      >
        {(condition.operation === Operation.UnKonwnCondition && (
          <div className="any-value">{localizedString('operation_unknown')}</div>
        )) ||
          ((condition.operation === Operation.RequireCondition ||
            condition.operation === Operation.NotEmptySetCondition) && (
              <div className="any-value">{localizedString('operation_any')}</div>
            )) ||
          (record.variable.length &&
            <ValueSwitch
              disabled={props.disabled}
              variable={record.variable}
              isBoolean={isBoolean}
              isTable={isTable}
              isMultiValue={isMultiValue}
              parameters={[
                ...props.selectedParameters,
                ...(props.dataQueries || []).map(dataQueryToParameter)
              ]}
              data={condition}
              onChange={(next: any[]) => {

                const nextData = cloneDeep(props.data);
                nextData[record.realIndex].conditions[record.contentIndex].value = next;
                props.onChange(nextData);
              }}
            />
          )}
        {
          <DeleteOutlined
            style={{
              position: 'absolute',
              right: 0,
              top: 12
            }}
            className="delete-btn hover-btn"
            onClick={() => {

              const nextData = cloneDeep(props.data);
              if (nextData[record.realIndex].conditions.length <= 1) {
                nextData.splice(record.realIndex);
              } else {
                nextData[record.realIndex].conditions.splice(index, 1);
              }

              props.onChange(nextData);

            }}
          />
        }
      </div>
    );
  };


  handleNewTriggerCondition = (e: any) => {
    const nextData = cloneDeep(this.props.data);

    nextData.push({
      kind: 'condition',
      variable: [],
      parameters: [],
      conditions: [{
        kind: 'content',
        operation: Operation.RequireCondition,
        value: ['']
      }]
    });

    this.props.onChange(nextData);
  }

  render() {

    const props = this.props;

    const rootPath = [{
      value: props.frame && props.frame.id || '',
      value_type: props.frame && props.frame.type
    }];

    return <NiTable
      style={props.style}
      className="condition-table"
      pagination={false}
      dataSource={this.flattenData(props.data)}
      rowKey={
        (record: ConditionRow) =>
          `condition-row-${record.realIndex}-${record.contentIndex}`
      }
      columns={
        [{
          title: localizedString('condition_variable'),
          width: '33%',
          dataIndex: 'variable',
          render: (item: Reference, record: ConditionRow, index: number) => {

            const obj = {
              children: <NiCommonCascader
                key="variable"
                disabled={props.disabled}
                selectionkey={SELECTION_KEY.AVAILABLE_CONDITION}
                rootpath={rootPath}
                value={item}
                displayRender={(label: string[], selectedOptions?: SelectionOption[]) => {
                  let variableName = renderSelectionVariable(item);
                  const isTableSize = record.operationAt === 'table_row';

                  if (isTableSize) {
                    variableName = variableName + '.size';
                  }

                  return variableName;
                }}
                onChange={(value: string[], selectedOptions?: SelectionOption[]) => {

                  const target = cloneDeep(props.data[record.realIndex]);
                  const variable: ReferenceNode[] = [];
                  const conditions: ConditionContent[] = [];
                  let isSize = false;
                  (selectedOptions || []).forEach((opt: SelectionOption) => {
                    if (opt.valueType === SpecialValueType.Size) {
                      conditions.push({
                        kind: 'content',
                        operationAt: 'table_row',
                        operation: Operation.GreaterThanCondition,
                        value: ['']
                      });
                      isSize = true;
                    } else if (opt.valueType === SpecialValueType.Index) {
                      const v = variable[variable.length - 1];
                      v.showIndex = true;
                      v.index = opt.index;

                    } else {
                      variable.push({
                        id: opt.value as string,
                        name: opt.label as string || '',
                        type: opt.valueType as ParameterType
                      });
                    }
                  });

                  if (isEqual(variable, target.variable)) {
                    return;
                  }

                  const leafNode = variable[variable.length - 1];

                  const param = ParameterMapStore.get(leafNode.id);

                  if (!isSize) {
                    conditions.push(param.multiValue
                      ? {
                        kind: 'content',
                        operation: Operation.NotEmptySetCondition,
                        value: []
                      }
                      : {
                        kind: 'content',
                        operation: Operation.RequireCondition,
                        value: ['']
                      });
                  }

                  target.variable = variable;
                  target.conditions = conditions;
                  const nextData = cloneDeep(props.data);
                  nextData[record.realIndex] = target;
                  props.onChange(nextData);
                }}
              />,
              props: {
                rowSpan: record.rowSpan
              }
            };

            return obj;
          }
        }, {
          title: localizedString('condition_operation'),
          className: 'trigger-table__operation',
          width: '33%',
          key: 'operator',
          dataIndex: 'operation',
          render: (operation: Operation, record: ConditionRow) => {

            if (!record.variable.length) {
              return '';
            }

            const isTable = record.operationAt === 'table_row' || false;

            const node = record && record.variable[record.variable.length - 1];
            const param = ParameterMapStore.get(node.id);
            const isMultiValue = !node.showIndex && param && param.multiValue || false;
            const isBoolean = param
              && param.proto
              && isSystemBoolean(param.proto) || false;

            return <OperationColumnComp
              disabled={props.disabled}
              index={record.contentIndex}
              data={props.data[record.realIndex]}
              isBoolean={isBoolean}
              isTable={isTable}
              isMultiValue={isMultiValue}
              record={props.data[record.realIndex].conditions[record.contentIndex]}
              handleOperationChange={(next: TriggerCondition) => {
                const nextData = cloneDeep(props.data);
                nextData[record.realIndex] = next;
                props.onChange(nextData);
              }}
            />;
          }
        },
        {
          title: localizedString('condition_value'),
          className: 'trigger-table__value',
          dataIndex: 'value',
          render: this.getValueComp
        },
        {
          title: props.disabled
            ? ''
            : <PlusOutlined
              onClick={this.handleNewTriggerCondition}
              className="add-condition-btn"
            />,
          width: 30,
          dataIndex: 'operation',
          className: 'hide',
          render: (operation: any, record: ConditionRow, index: number) => {
            if (!record.variable.length) {
              return '';
            }

            const node = record && record.variable[record.variable.length - 1];
            const param = ParameterMapStore.get(node.id);
            const isMultiValue = !node.showIndex && param && param.multiValue || false;

            return <div
              className="icon-area"
            >
              {!props.disabled &&
                <PlusOutlined className="plus-btn hover-btn" onClick={(e: any) => {
                  e.stopPropagation();

                  const nextData = cloneDeep(props.data);

                  nextData[record.realIndex].conditions.push({
                    kind: 'content',
                    operation: isMultiValue
                      ? Operation.NotEmptySetCondition
                      : Operation.RequireCondition,
                    value: []
                  });

                  props.onChange(nextData);
                }}
                />
              }
            </div>;
          }
        }]
      }
    />;
  }
}
